/* We were initially using the react-masonry-component (https://www.npmjs.com/package/react-masonry-component)
for rendering the masonry layout, but this library is not supported by React 18. 
The alternative was to implement a custom solution using the core library
that is also used by react-masonry-component, i.e. masonry-layout (https://www.npmjs.com/package/masonry-layout).
The approach below partially follows the approach used 
here - https://github.com/eiriklv/react-masonry-component/blob/master/lib/index.js - which could be used for further
references if any more changes are needed to our logic in the future.
The library was first imported in order to get the type and then the main library had to be conditionally
imported so it is only used on the client side, otherwise this will crash when used with 
the server in place since the window object used within will be undefined on the server side. */

import MasonryLayoutType from 'masonry-layout';
import { RefObject, useCallback, useEffect, useRef } from 'react';

const MasonryLayout =
  typeof window !== 'undefined' ? require('masonry-layout') : null;

interface MasonryConfig {
  readonly columnWidth: string | number;
  readonly itemSelector: string;
  readonly gutter: number;
  readonly horizontalOrder: boolean;
}

const useMasonryLayout = (
  containerRef: RefObject<HTMLElement>,
  { columnWidth, itemSelector, gutter, horizontalOrder }: MasonryConfig
) => {
  const masonryInstance = useRef<MasonryLayoutType | null>(null);
  const latestKnownItems = useRef<HTMLElement[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  const getCurrentItems = useCallback(() => {
    if (!containerRef.current) return [];

    return Array.from(
      containerRef.current.querySelectorAll(itemSelector)
    ) as HTMLElement[];
  }, [itemSelector]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  const initializeMasonry = useCallback(async () => {
    if (!containerRef.current) return;

    masonryInstance.current = new MasonryLayout(containerRef.current, {
      columnWidth,
      itemSelector,
      gutter,
      horizontalOrder,
    });
    latestKnownItems.current = getCurrentItems();
  }, [columnWidth, itemSelector, gutter, horizontalOrder, getCurrentItems]);

  useEffect(() => {
    initializeMasonry();

    return () => {
      masonryInstance.current?.destroy?.();
    };
  }, [initializeMasonry]);

  useEffect(() => {
    const currentItems = getCurrentItems();
    const newItems = currentItems.filter(
      (item) => latestKnownItems.current.indexOf(item) === -1
    );
    const pivotIndex = latestKnownItems.current[0]
      ? currentItems.indexOf(latestKnownItems.current[0])
      : 0;
    const prependedItems = newItems.filter(
      (item) => currentItems.indexOf(item) < pivotIndex
    );
    const appendedItems = newItems.filter(
      (item) => prependedItems.indexOf(item) === -1
    );
    const removedItems = latestKnownItems.current.filter(
      (item) => currentItems.indexOf(item) === -1
    );

    latestKnownItems.current = currentItems;

    if (removedItems.length) {
      masonryInstance.current?.remove?.(removedItems);
    }
    if (appendedItems.length) {
      masonryInstance.current?.appended?.(appendedItems);
    }
    if (prependedItems.length) {
      masonryInstance.current?.prepended?.(prependedItems);
    }

    masonryInstance.current?.reloadItems?.();
    masonryInstance.current?.layout?.();
  });
};

export default useMasonryLayout;
